/**
 * Breadcrumb - fil d ariane affiche dans la TopBar.
 *
 * Lit useLocation, mappe le path sur un label + icone + parent eventuel.
 * Gere les routes parametrees (ex: /scans/:scanId → "Scans > Scan abc123").
 */
import { useLocation, Link } from 'react-router-dom';
import { tokens } from '@fluentui/react-components';
import {
  Home24Regular,
  DataPie24Regular,
  ShieldCheckmark24Regular,
  ArrowSync24Regular,
  DocumentTable24Regular,
  Building24Regular,
  WalletCreditCard24Regular,
  Settings24Regular,
  BookGlobe24Regular,
  Key24Regular,
  Alert24Regular,
  People24Regular,
  HandshakeRegular,
  PlugConnected24Regular,
  ChevronRight20Regular,
} from '@fluentui/react-icons';

interface Crumb {
  label: string;
  path?: string;        // si defini → cliquable
  icon?: React.ReactNode;
}

/** Map d un segment de path vers son crumb. */
const ROUTE_META: Record<string, Crumb> = {
  '/':            { label: 'Tableau de bord',         icon: <DataPie24Regular /> },
  '/controls':    { label: 'Catalogue de contrôles',  icon: <ShieldCheckmark24Regular /> },
  '/cyfun':       { label: 'Mapping CyFun 🇧🇪',       icon: <BookGlobe24Regular /> },
  '/scans':       { label: 'Scans',                   icon: <ArrowSync24Regular /> },
  '/reports':     { label: 'Rapports',                icon: <DocumentTable24Regular /> },
  '/alerts':      { label: 'Alertes',                 icon: <Alert24Regular /> },
  '/tenants':     { label: 'Organisation',            icon: <Building24Regular /> },
  '/users':       { label: 'Utilisateurs',            icon: <People24Regular /> },
  '/billing':     { label: 'Abonnement',              icon: <WalletCreditCard24Regular /> },
  '/license':     { label: 'Licence',                 icon: <Key24Regular /> },
  '/settings':    { label: 'Paramètres',              icon: <Settings24Regular /> },
  '/partners':    { label: 'Partenaires',             icon: <HandshakeRegular /> },
  '/onboarding':  { label: 'Connexion Microsoft 365', icon: <PlugConnected24Regular /> },
};

export function Breadcrumb() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  // Build crumbs en partant de la racine
  const crumbs: Crumb[] = [];

  // Toujours commencer par Home si on n y est pas
  if (location.pathname !== '/') {
    crumbs.push({ label: 'Accueil', path: '/', icon: <Home24Regular /> });
  } else {
    crumbs.push({ label: 'Tableau de bord', icon: <DataPie24Regular /> });
    return renderBreadcrumb(crumbs);
  }

  if (segments.length === 0) return renderBreadcrumb(crumbs);

  // 1er segment → categorie principale
  const root = `/${segments[0]}`;
  const rootMeta = ROUTE_META[root];
  if (rootMeta) {
    // Si plus de segments → c est un sous-ecran, le root devient cliquable
    crumbs.push({
      ...rootMeta,
      path: segments.length > 1 ? root : undefined,
    });
  } else {
    // Route inconnue → on affiche juste le segment brut
    crumbs.push({ label: segments[0] });
  }

  // Sous-segments : 2eme niveau (ex: /scans/scan-xxx, /controls/IDA-001)
  if (segments.length > 1) {
    const last = segments[segments.length - 1];
    // Si c est un id genere → on l affiche tronque
    if (last.length > 10 && /[a-f0-9]{6,}/i.test(last)) {
      crumbs.push({ label: `${last.substring(0, 12)}…` });
    } else {
      crumbs.push({ label: last });
    }
  }

  return renderBreadcrumb(crumbs);
}

function renderBreadcrumb(crumbs: Crumb[]) {
  return (
    <nav
      aria-label="Fil d Ariane"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: tokens.colorNeutralForeground2,
        flexWrap: 'wrap',
      }}
    >
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        const content = (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: isLast ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground3,
            fontWeight: isLast ? 600 : 500,
          }}>
            {c.icon && <span style={{
              display: 'inline-flex',
              fontSize: 14,
              color: isLast ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
            }}>{c.icon}</span>}
            <span>{c.label}</span>
          </span>
        );
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {c.path && !isLast ? (
              <Link to={c.path} style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 6,
                padding: '4px 8px',
                transition: 'background-color 150ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = tokens.colorNeutralBackground3; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {content}
              </Link>
            ) : (
              <span style={{ padding: '4px 8px' }}>{content}</span>
            )}
            {!isLast && (
              <ChevronRight20Regular
                style={{
                  color: tokens.colorNeutralForeground4,
                  flexShrink: 0,
                }}
              />
            )}
          </span>
        );
      })}
    </nav>
  );
}
